import type { Row, Table } from '@tanstack/react-table'
import { Maximize2Icon, Minimize2Icon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { TableExport } from '@/components'
import {
  Button,
  DataTable,
  type DataTableColumns,
  FullScreenContainer,
} from '@/components/ui'
import type { TableData } from '@/components/ui/table/DataTable/types'
import type { MerchantStat } from '@/modules/projects/stores/types'
import { cn } from '@/utils'
import { Section } from '../shared'
import { merchantsColumns } from './columns'
import type { MerchantRow } from './types'
import { merchantNames } from './utils'

interface MerchantsSectionProps {
  merchants: MerchantStat[]
  onMerchantClick?: (merchantGroup: string) => void
  exportFilename?: string
}

function toMerchantRows(merchants: MerchantStat[]): MerchantRow[] {
  return merchants
    .map((merchant) => ({
      ...merchant,
      id: merchant.merchantGroup,
      variationCount: merchantNames(merchant).length,
      avgAmount: merchant.count > 0 ? merchant.amount / merchant.count : 0,
    }))
    .sort((first, second) => Math.abs(second.amount) - Math.abs(first.amount))
}

export function MerchantsSection({
  merchants,
  onMerchantClick,
  exportFilename = 'merchants',
}: MerchantsSectionProps) {
  const rows = useMemo(() => toMerchantRows(merchants), [merchants])
  const [table, setTable] = useState<Table<any> | null>(null)
  const [isFullScreen, setIsFullScreen] = useState(false)

  return (
    <FullScreenContainer isActive={isFullScreen}>
      <Section
        title="Merchants"
        description="One business can show up under several names on a statement (e.g. STARBUCKS #123, Starbucks Coffee, SBUX). We bundled those variations into a single merchant. Click a row to see every transaction that rolled up into it."
        className={cn(isFullScreen && 'flex min-h-0 flex-1 flex-col')}
      >
        <div className="flex items-center justify-end gap-2 border-b px-6 py-3">
          {table && <TableExport table={table} filename={exportFilename} />}
          <Button
            theme="outline"
            size="sm"
            isIcon
            onClick={() => setIsFullScreen(!isFullScreen)}
          >
            {isFullScreen ? (
              <Minimize2Icon className="h-4 w-4" />
            ) : (
              <Maximize2Icon className="h-4 w-4" />
            )}
          </Button>
        </div>
        <DataTable
          columns={
            merchantsColumns(
              merchants.some((merchant) => merchant.moneyIn !== undefined),
            ) as DataTableColumns
          }
          data={rows}
          height={isFullScreen ? 'flex-1 min-h-0' : 'h-auto'}
          enableRowSelection={false}
          emptyMessage="No merchant data."
          initialPageSize={25}
          pageSizes={[25, 50, 100]}
          onTableReady={setTable}
          onRowClick={(row: Row<TableData>) => {
            const merchantRow = row.original as unknown as MerchantRow
            onMerchantClick?.(merchantRow.merchantGroup)
          }}
        />
      </Section>
    </FullScreenContainer>
  )
}
